import { collection, getDocs, limit, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { HiOutlineDocumentText } from 'react-icons/hi2';
import { Link, useParams } from 'react-router-dom';
import { db } from '../../App';
import ShareButton from '../../components/ShareButton';
import { DocumentElementType } from '../../global/types';

const DocumentDetails = () => {
    const { documentSlug } = useParams();
    const [documentItem, setDocumentItem] = useState<DocumentElementType | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;

        const loadDocument = async () => {
            setLoading(true);

            try {
                if(!documentSlug) throw new Error('not-found');

                const snapshot = await getDocs(query(
                    collection(db, 'mediatheque'),
                    where('slug', '==', documentSlug),
                    limit(1),
                ));
                const documentSnapshot = snapshot.docs[0];

                if(!documentSnapshot || documentSnapshot.data().category !== 'documents') {
                    throw new Error('not-found');
                }

                if(active) {
                    setDocumentItem({
                        ...documentSnapshot.data(),
                        id: Number(documentSnapshot.id),
                    } as DocumentElementType);
                }
            } catch {
                if(active) setDocumentItem(null);
            } finally {
                if(active) setLoading(false);
            }
        };

        loadDocument();
        return () => { active = false; };
    }, [documentSlug]);

    useEffect(() => {
        if(!documentItem?.title) return;
        const previousTitle = document.title;
        document.title = `${documentItem.title} | FLAG`;
        return () => { document.title = previousTitle; };
    }, [documentItem]);

    if(loading) {
        return <div className="mx-auto my-[10vh] rounded-xl bg-white p-8 text-gray-500">Chargement...</div>;
    }

    if(!documentItem) {
        return (
            <div className="mx-auto my-[10vh] max-w-xl rounded-xl bg-white p-8 text-center">
                <h1 className="mb-3 text-2xl font-medium">Document introuvable</h1>
                <p className="mb-5 text-gray-600">Ce lien est incorrect ou le document n'est plus disponible.</p>
                <Link className="text-primary underline" to="/mediatheque/documents">
                    Retour aux documents
                </Link>
            </div>
        );
    }

    const downloadUrl = documentItem.documentUrl || documentItem.fileUrl;

    return (
        <article className="mx-2 my-6 max-w-4xl rounded-xl bg-white p-5 sm:p-8 lg:mx-auto lg:my-[10vh]">
            <Link className="text-sm text-gray-600 hover:text-primary hover:underline" to="/mediatheque/documents">
                Retour aux documents
            </Link>

            <div className="mt-6 flex items-start gap-4">
                <HiOutlineDocumentText aria-hidden="true" className="shrink-0 text-red-800" size={56} />
                <div>
                    <h1 className="text-2xl font-medium text-gray-900 lg:text-3xl">{documentItem.title}</h1>
                    {documentItem.description ? (
                        <p className="mt-4 leading-7 text-gray-600">{documentItem.description}</p>
                    ) : null}
                </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3 border-t border-gray-200 pt-5">
                {downloadUrl ? (
                    <a className="customButton text-sm" href={downloadUrl} download>
                        Télécharger le document
                    </a>
                ) : (
                    <p className="text-sm text-gray-500">Aucun fichier n'est associé à ce document.</p>
                )}
                <ShareButton title={documentItem.title} />
            </div>
        </article>
    );
};

export default DocumentDetails;
